import { Link } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useProfile, useNamespaces, useTenantAnalytics } from '../lib/hooks'

export function Dashboard() {
  const profile   = useProfile()
  const ns        = useNamespaces()
  const analytics = useTenantAnalytics()

  return (
    <Layout title="Dashboard">
      {/* Stat strip */}
      <div className="stat-strip fu" style={{ marginBottom: 24 }}>
        <div className="stat-cell">
          <div className="stat-num">
            {analytics.loading ? '—' : (analytics.data?.total_downloads ?? 0).toLocaleString()}
          </div>
          <div className="stat-label">Total downloads</div>
        </div>
        <div className="stat-cell">
          <div className="stat-num">
            {analytics.loading ? '—' : (analytics.data?.downloads_last_30_days ?? 0).toLocaleString()}
          </div>
          <div className="stat-label">Last 30 days</div>
        </div>
        <div className="stat-cell">
          <div className="stat-num">{ns.loading ? '—' : (ns.data?.namespaces.length ?? 0)}</div>
          <div className="stat-label">Namespaces</div>
        </div>
        <div className="stat-cell">
          <div className="stat-num">
            {ns.loading ? '—' : (ns.data?.namespaces.reduce((s, n) => s + n.module_count, 0) ?? 0)}
          </div>
          <div className="stat-label">Modules</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Namespaces */}
        <div className="card fu">
          <div className="card-header">
            <div>
              <div className="kicker">Workspaces</div>
              <h2 style={{ fontSize: '0.95rem' }}>Your namespaces</h2>
            </div>
            <Link to="/namespaces" className="btn btn-ghost btn-sm">View all</Link>
          </div>
          {ns.loading && <div className="empty-state">Loading…</div>}
          {ns.error && <div className="empty-state" style={{ color: 'var(--red)' }}>{ns.error}</div>}
          {ns.data?.namespaces.slice(0, 5).map((n) => (
            <Link key={n.id} to={`/namespaces/${n.slug}`} style={rowStyle}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text)' }}>
                  {n.slug}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 2 }}>
                  {n.module_count} modules · {n.member_count} members
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span className={`badge ${n.is_private ? 'badge-neutral' : 'badge-green'}`}>
                  {n.is_private ? 'private' : 'public'}
                </span>
                <span className="badge badge-accent">{n.role}</span>
              </div>
            </Link>
          ))}
          {ns.data?.namespaces.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">⬡</div>
              <div>No namespaces yet</div>
              <Link to="/namespaces" className="btn btn-ghost btn-sm" style={{ marginTop: 4 }}>Create one</Link>
            </div>
          )}
        </div>

        {/* Top modules */}
        <div className="card fu">
          <div className="card-header">
            <div>
              <div className="kicker">Activity</div>
              <h2 style={{ fontSize: '0.95rem' }}>Most downloaded</h2>
            </div>
          </div>
          {analytics.loading && <div className="empty-state">Loading…</div>}
          {analytics.data?.top_modules.slice(0, 5).map((m, i) => (
            <Link
              key={i}
              to={`/namespaces/${m.namespace}/modules/${m.name}/${m.provider}`}
              style={rowStyle}
            >
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text)' }}>
                  {m.namespace}/{m.name}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 2 }}>
                  {m.provider}{m.latest_version ? ` · v${m.latest_version}` : ''}
                </div>
              </div>
              <div style={{ fontFamily: 'var(--head)', fontSize: '1rem', color: 'var(--accent)' }}>
                {m.download_count.toLocaleString()}
              </div>
            </Link>
          ))}
          {!analytics.loading && (analytics.data?.top_modules.length ?? 0) === 0 && (
            <div className="empty-state">
              <div className="empty-icon">▲</div>
              <div>No downloads yet</div>
            </div>
          )}
        </div>
      </div>

      {/* First-login welcome */}
      {!profile.loading && profile.data?.is_new_user && (
        <div style={welcomeStyle} className="fu">
          <div>
            <div className="kicker">Getting started</div>
            <h2 style={{ fontSize: '1rem', marginBottom: 6 }}>
              Your namespace is{' '}
              <span style={{ color: 'var(--accent)' }}>{profile.data.default_namespace.slug}</span>
            </h2>
            <p className="lead" style={{ fontSize: '0.8rem' }}>
              Add this to your{' '}
              <code style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>.terraformrc</code>:
              {' '}credentials for{' '}
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>
                {window.location.hostname}
              </span>
            </p>
          </div>
          <Link
            to={`/namespaces/${profile.data.default_namespace.slug}/publish`}
            className="btn btn-primary"
          >
            Publish first module
          </Link>
        </div>
      )}
    </Layout>
  )
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '11px 18px',
  borderBottom: '1px solid rgba(255,255,255,0.03)',
  textDecoration: 'none',
  transition: 'background 0.1s',
}

const welcomeStyle: React.CSSProperties = {
  marginTop: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  background: 'var(--accent-d)',
  border: '1px solid rgba(249,115,22,0.2)',
  borderRadius: 'var(--radius-lg)',
}
